/**
 * API error envelope per the documented response shape (every doc lists a
 * { code, message } pair under non-2xx responses). The full set of codes
 * the admin console handles is in plan.md §3.5; this module gives them
 * structured handling so forms/routes can branch.
 */

import { z, ZodError } from 'zod';
import { formatZodIssues } from '@/schemas/zod-helpers';

const errorEnvelopeSchema = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
  detail: z.unknown().optional(),
});

export type ApiErrorCode =
  // generic
  | 'invalid_json'
  | 'invalid_input'
  // a 2xx body that breaks the documented schema — never retried
  | 'invalid_response'
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'rate_limited'
  | 'not_found'
  | 'not_implemented'
  | 'conflict'
  | 'server_error'
  | 'network'
  | 'unknown'
  // auth-specific
  | 'invalid_credentials'
  | 'invalid_token'
  | 'invalid_code'
  | 'already_verified'
  | 'mfa_not_enrolled'
  | 'mfa_required'
  // string fallback for codes the docs add later
  | (string & {});

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly detail: unknown;

  constructor(input: { status: number; code: ApiErrorCode; message: string; detail?: unknown }) {
    super(input.message);
    this.name = 'ApiError';
    this.status = input.status;
    this.code = input.code;
    this.detail = input.detail;
  }

  /** True for non-recoverable auth failures (token expired, refresh dead, etc.). */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }

  /** True for forbidden — usually means MFA re-attestation required. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/**
 * Build an ApiError from any thrown value emitted by the ky pipeline.
 * Designed for ky's HTTPError, plain TypeError on network failure, and our
 * own throws.
 */
export async function toApiError(thrown: unknown): Promise<ApiError> {
  if (thrown instanceof ApiError) return thrown;
  if (thrown instanceof ZodError) return invalidResponse(thrown);

  // ky.HTTPError exposes `.response`. We deliberately don't import ky's type
  // here — duck-type instead so the schema stays loose for tests/MSW.
  if (typeof thrown === 'object' && thrown !== null && 'response' in thrown) {
    const response = (thrown as { response: Response }).response;
    let body: unknown = null;
    try {
      const text = await response.clone().text();
      body = text.length > 0 ? JSON.parse(text) : null;
    } catch {
      body = null;
    }
    const parsed = errorEnvelopeSchema.safeParse(body);
    const envelope = parsed.success ? parsed.data : null;
    const code: ApiErrorCode = envelope?.code ?? mapStatusToCode(response.status);
    const message =
      envelope?.message ??
      (thrown as { message?: string }).message ??
      (response.statusText.length > 0 ? response.statusText : 'Request failed');
    return new ApiError({
      status: response.status,
      code,
      message,
      detail: envelope?.detail ?? body,
    });
  }

  if (thrown instanceof TypeError) {
    return new ApiError({ status: 0, code: 'network', message: thrown.message });
  }

  const message = thrown instanceof Error ? thrown.message : String(thrown);
  return new ApiError({ status: 0, code: 'unknown', message });
}

/**
 * A 2xx body that does not match its documented schema. Deterministic, so the
 * query layer never retries it, and it carries the issue paths so the screen
 * can show what did not match.
 */
export function invalidResponse(error: ZodError): ApiError {
  return new ApiError({
    status: 0,
    code: 'invalid_response',
    message: 'Response shape did not match the expected schema',
    detail: { issues: formatZodIssues(error, 5) },
  });
}

/** The issue lines an `invalid_response` carries, or `[]`. */
export function invalidResponseIssues(err: unknown): string[] {
  if (!(err instanceof ApiError) || err.code !== 'invalid_response') return [];
  const issues = (err.detail as { issues?: unknown } | null)?.issues;
  return Array.isArray(issues) ? issues.filter((i): i is string => typeof i === 'string') : [];
}

function mapStatusToCode(status: number): ApiErrorCode {
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 405 || status === 501) return 'not_implemented';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown';
}
